// Copyright 1999-2026. WebPros International GmbH. All rights reserved.

import { Button, Icon, Label, Text, useTranslate } from '@plesk/ui-library';
import React, { useId, useState } from 'react';
import type { ToolCall } from '../../../CopilotContext';
import type { ToolCallDecision, ToolCallPayload } from './ToolCallContext';
import ToolCallDetail, { RawPayloadState } from './ToolCallDetail';
import ToolCallGlyph from './ToolCallGlyph';
import { addToolCallToast } from './toolCallToast';
import { ToolCallState, isTruncated } from './toolCalls';

const CHIP_KEYS = {
    AUTO_READONLY: 'tool-call.chip.auto',
    AUTO_YOLO: 'tool-call.chip.auto',
    APPROVED: 'tool-call.chip.approved',
    REJECTED: 'tool-call.chip.rejected',
} as const;

// Calls that did not, or not yet, do anything
const MUTED_STATES: ToolCallState[] = ['queued', 'failed-notfound', 'rejected'];

type ToolCallRowProps = {
    call: ToolCall;
    state: ToolCallState;
    // The call's decide request is in flight
    deciding: boolean;
    onDecide: (decision: ToolCallDecision['decision']) => void;
    fetchResult: () => Promise<ToolCallPayload>;
};

const ToolCallRow = ({ call, state, deciding, onDecide, fetchResult }: ToolCallRowProps) => {
    const translate = useTranslate('components.copilot');
    const detailId = useId();
    const [expanded, setExpanded] = useState(false);
    const [rawOpen, setRawOpen] = useState(false);
    // Kept by the row, so that collapsing it does not fetch the payload again
    const [raw, setRaw] = useState<RawPayloadState>({ status: 'idle' });

    const toggleExpanded = () => setExpanded(value => !value);

    // The whole line is the pointer target; the button inside it is the control, and the
    // decision buttons handle their own clicks
    const onLineClick = (event: React.MouseEvent) => {
        if (!(event.target as HTMLElement).closest('button')) {
            toggleExpanded();
        }
    };

    const toggleRaw = () => {
        const open = !rawOpen;
        setRawOpen(open);
        if (open && raw.status === 'idle') {
            setRaw({ status: 'loading' });
            fetchResult().then(
                payload => setRaw({ status: 'loaded', payload }),
                () => {
                    setRaw({ status: 'idle' });
                    setRawOpen(false);
                    addToolCallToast('danger', String(translate('tool-call.error.generic')));
                },
            );
        }
    };

    const outcomeKeys: Partial<Record<ToolCallState, string>> = {
        queued: 'tool-call.outcome.queued',
        running: 'tool-call.outcome.running',
        rejected: 'tool-call.outcome.rejected',
    };
    const outcomeKey = outcomeKeys[state];
    // None while pending; the mechanical outcome, or the error summary, once executed
    let outcome = state === 'pending' ? null : call.outcome;
    if (outcomeKey) {
        outcome = String(translate(outcomeKey));
    }

    const chip = call.decision ? translate(CHIP_KEYS[call.decision]) : null;

    return (
        <li className={`plesk-copilot-tool-call plesk-copilot-tool-call--${state}`} data-testid="copilot-tool-call">
            {/* Pointer only: keyboard and assistive technology use the disclosure button inside */}
            {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
            <div className="plesk-copilot-tool-call__line" onClick={onLineClick}>
                <button
                    type="button"
                    className="plesk-copilot-tool-call__toggle"
                    aria-expanded={expanded}
                    aria-controls={detailId}
                    onClick={toggleExpanded}
                >
                    <ToolCallGlyph state={state} label={String(translate(`tool-call.status.${state}`))} variant="row" />
                    <span className="plesk-copilot-tool-call__text">
                        <Text className="plesk-copilot-tool-call__label" intent={MUTED_STATES.includes(state) ? 'muted' : undefined}>
                            {call.label}
                        </Text>
                        {outcome !== null && (
                            <Text className="plesk-copilot-tool-call__outcome" intent={state === 'failed-error' ? 'danger' : 'muted'}>
                                {outcome}
                                {state === 'succeeded' && isTruncated(call) && <>{' · '}{translate('tool-call.clipped')}</>}
                            </Text>
                        )}
                    </span>
                    {chip && <Label className="plesk-copilot-tool-call__chip" view="outline" caps={false}>{chip}</Label>}
                </button>
                {state === 'pending' && (
                    <span className="plesk-copilot-tool-call__actions">
                        <Button
                            disabled={deciding}
                            onClick={() => onDecide('REJECTED')}
                            data-tool-call-action="reject"
                        >
                            {translate('tool-call.reject')}
                        </Button>
                        <Button
                            intent="primary"
                            disabled={deciding}
                            onClick={() => onDecide('APPROVED')}
                            data-tool-call-action="approve"
                        >
                            {translate('tool-call.approve')}
                        </Button>
                    </span>
                )}
                <span className="plesk-copilot-tool-call__caret" aria-hidden="true">
                    <Icon name="chevron-right" intent="inactive" rotate={expanded ? 90 : undefined} />
                </span>
            </div>
            {/* Mounted while collapsed too, as the target of aria-controls; its content is not */}
            <div id={detailId} className="plesk-copilot-tool-detail" hidden={!expanded} data-testid="copilot-tool-call-detail">
                {expanded && (
                    <ToolCallDetail
                        call={call}
                        state={state}
                        rawOpen={rawOpen}
                        onToggleRaw={toggleRaw}
                        raw={raw}
                    />
                )}
            </div>
        </li>
    );
};

export default ToolCallRow;
